import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import AuthModal from '../../components/AuthModal/AuthModal';
import { EmptyShareState, PublicPageCTA, PublicShareLayout } from '../../components/PublicShare/PublicShareLayout';
import ShareMenu from '../../components/PublicShare/ShareMenu';
import AuthGateAction from '../../components/PublicShare/AuthGateAction';
import { CoinPreview, PosterStrip } from '../../components/PublicShare/PreviewCards';
import AppPromotionCard from '../../components/AppPromotionCard/AppPromotionCard';
import { useAuth } from '../../context/AuthContext';
import { useAuthGate } from '../../hooks/useAuthGate';
import { PublicShareService } from '../../services/publicShareService';
import { AUTH_MESSAGES } from '../../utils/authMessages';
import { buildOgImageUrl, useShareMetadata } from '../../utils/shareMetadata';
import styles from '../../components/PublicShare/PublicShare.module.css';
import { getShareAsset, SHARE_DESTINATIONS } from '../../utils/coinMedia';
import { seasonalEditionLabel, withSeasonLabel } from '@aluvo/constants/src/collectionAvailability';
import { progressLabel, unlockFraction } from '@aluvo/constants/src/collectionCompletion';

export default function PublicCollectionShare() {
    const { collectionId } = useParams();
    const [searchParams] = useSearchParams();
    const username = searchParams.get('u') || searchParams.get('username');
    const navigate = useNavigate();
    const { user } = useAuth();
    const { gateAction, authModalProps } = useAuthGate(user);
    const [payload, setPayload] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        let mounted = true;
        PublicShareService.getCollection(collectionId, username)
            .then((data) => mounted && setPayload(data))
            .catch((err) => mounted && setError(err.message || 'This collection is not available right now.'));
        return () => { mounted = false; };
    }, [collectionId, username]);

    const collection = payload?.collection;
    const share = useMemo(() => {
        if (!collection) return { title: 'Aluvo Collection | Aluvo', description: 'A collection on Aluvo.' };
        const owner = collection.user?.username;
        const completedCopy = withSeasonLabel(owner && collection.completed
            ? `@${owner} completed ${collection.title} on Aluvo 💎`
            : owner
                ? `@${owner} is ${collection.remaining} title${collection.remaining === 1 ? '' : 's'} away from unlocking ${collection.title} on Aluvo.`
                : `Complete ${collection.title} on Aluvo and unlock the reward coin.`, collection);
        return {
            url: `${window.location.origin}/collection/${collection.slug || collection.id}${owner ? `?u=${owner}` : ''}`,
            title: owner && collection.completed ? `${owner} completed ${collection.title} 💎 | Aluvo` : `${collection.title} Collection | Aluvo`,
            description: collection.completed ? `${progressLabel(collection)}. Legendary collection reward unlocked.` : collection.description,
            text: completedCopy,
            tier: collection.reward?.tier,
            image: getShareAsset({ coin: collection.reward, destination: SHARE_DESTINATIONS.LINK_PREVIEW })?.url || buildOgImageUrl({ title: collection.title, subtitle: completedCopy, username: owner, tier: collection.reward?.tier, theme: collection.completed ? 'legendary' : 'cinematic' }),
        };
    }, [collection]);

    useShareMetadata(share);

    if (error) return <EmptyShareState icon="🏛️" title="This collection is not available right now." message="The shared collection may have been removed or disabled." />;
    if (!payload) return <EmptyShareState icon="🏛️" title="Loading collection" message="Checking required titles and reward status..." />;
    if (payload.isPrivate) return <EmptyShareState icon="🔒" title="This collector's profile is private." message="This collection progress is not public right now." />;

    const completed = collection.completed;
    const headline = collection.user?.username
        ? completed
            ? `@${collection.user.username} completed ${collection.title}.`
            : `@${collection.user.username} is ${collection.remaining} ${collection.remaining === 1 ? 'movie' : 'titles'} away from unlocking ${collection.title}.`
        : collection.title;
    const edition = seasonalEditionLabel(collection);
    const progressPercent = Math.round(unlockFraction(collection) * 100);

    return (
        <PublicShareLayout theme={completed ? 'legendary' : 'cinematic'} eyebrow="Collection Proof">
            <section className={styles.heroCard}>
                <div>
                    <span className={styles.statusPill}>{completed ? 'Reward unlocked' : 'Collection in progress'}</span>
                    {edition && <span className={styles.pill} data-testid="season-edition">{edition}</span>}
                    <h1 className={styles.title}>{headline}</h1>
                    <p className={styles.subtitle}>{collection.description}</p>
                    <div className={styles.progressRow}>
                        <strong>{progressLabel(collection)}</strong>
                        {completed && <span className={styles.pill}>Completed {formatDate(collection.completedAt)}</span>}
                    </div>
                    <div className={styles.progressBar}><div className={styles.progressFill} style={{ width: `${progressPercent}%` }} /></div>
                    <div className={styles.heroActions}>
                        <ShareMenu {...share} coin={collection.reward} compact />
                        <a className={styles.secondaryAction} href="#required">View Required Titles</a>
                        {collection.reward?.coinId && <Link className={styles.ghostAction} to={`/coin/${collection.reward.coinId}`}>View Reward Coin</Link>}
                        <AuthGateAction className={styles.primaryAction} gateAction={gateAction} authMessage={AUTH_MESSAGES.COLLECTION} onClick={() => navigate('/collections')}>Start Collection</AuthGateAction>
                    </div>
                </div>
                <div className={styles.panel}>
                    <CoinPreview animate coin={{ tier: collection.reward?.tier, coinImageUrl: collection.reward?.coinImageUrl, media: collection.reward?.media, title: collection.reward?.name }} size="lg" legendary={completed || collection.reward?.tier === 'legendary'} />
                    <p className={styles.muted}>{collection.reward?.locked ? 'Reward locked until the set is complete.' : `${collection.reward?.name} unlocked.`}</p>
                </div>
            </section>

            <section id="required" className={styles.panel}>
                <h2>Required titles</h2>
                <PosterStrip items={collection.items} />
            </section>

            <AppPromotionCard variant="share_page" compact />
            <PublicPageCTA title="Create your Aluvo account and start completing collections." text="Verify required titles, unlock collection rewards, and share your movie & TV mastery." buttonText="Start completing collections" />
            <AuthModal {...authModalProps} />
        </PublicShareLayout>
    );
}

function formatDate(date) {
    if (!date) return 'recently';
    return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', year: 'numeric' }).format(new Date(date));
}